/**
 * The team dashboard queries (openspec cs-team-dashboards). Keys sit under ['customerService'],
 * so a status change made from a dashboard refreshes them like the rest of the screen.
 */
import { useQuery } from '@tanstack/react-query';
import { tokenHandler } from '@/utils/tokenHandler';
import { teamDashboardApi } from '../api/teamDashboardApi';
import { monthToDate, pickupWindow, UNBILLED_SINCE, vesselWindow } from '../model/dashboardWindows';
import type { DateType } from '../types/customerService';
import type { TeamScope } from '../types/teamDashboard';

const STALE_TIME = 30_000;
const CACHE_TIME = 5 * 60 * 1000;

const common = {
  staleTime: STALE_TIME,
  gcTime: CACHE_TIME,
  refetchOnWindowFocus: false,
  // A 403 or 404 is an answer, not a hiccup: do not ask three more times.
  retry: false,
} as const;

export const teamKeys = {
  all: ['customerService', 'team'] as const,
  view: (employeeId: number | null) => [...teamKeys.all, 'view', employeeId ?? 0] as const,
  directory: (department: string) => [...teamKeys.all, 'directory', department] as const,
  search: (text: string) => [...teamKeys.all, 'search', text] as const,
  pickups: (employeeId: number | null, scope: TeamScope, dateType: DateType) =>
    [...teamKeys.all, 'pickups', employeeId ?? 0, scope, dateType] as const,
  vessels: (employeeId: number | null, scope: TeamScope, dateType: DateType) =>
    [...teamKeys.all, 'vessels', employeeId ?? 0, scope, dateType] as const,
  counts: (employeeId: number | null, scope: TeamScope) => [...teamKeys.all, 'counts', employeeId ?? 0, scope] as const,
  status: (employeeId: number | null, scope: TeamScope) => [...teamKeys.all, 'status', employeeId ?? 0, scope] as const,
};

/** Whose dashboard this is, their team and the breadcrumb; the gate for everything else. */
export const useTeamView = (employeeId: number | null, enabled = true) =>
  useQuery({
    queryKey: teamKeys.view(employeeId),
    queryFn: () => teamDashboardApi.getView(employeeId),
    enabled,
    ...common,
  });

/**
 * True when the signed-in employee leads anyone (RulesTypeMaster), for menus. Asked only with a
 * token that names the employee (`pid`): an older token would get a 401 and end the session.
 */
export const useIsTeamLeader = (enabled: boolean): boolean => {
  const named = Boolean(tokenHandler.decodePayload()?.pid);
  return useTeamView(null, enabled && named).data?.leader ?? false;
};

/** A Super Admin's starting list for one department, and its people in no team. */
export const useTeamDirectory = (department: string, enabled: boolean) =>
  useQuery({
    queryKey: teamKeys.directory(department),
    queryFn: () => teamDashboardApi.getDirectory(department),
    enabled,
    ...common,
  });

/** People the signed-in employee may open, by name; from two letters. */
export const useTeamSearch = (text: string) =>
  useQuery({
    queryKey: teamKeys.search(text.trim().toLowerCase()),
    queryFn: () => teamDashboardApi.search(text.trim()),
    enabled: text.trim().length >= 2,
    placeholderData: (previous) => previous,
    ...common,
  });

export const useTeamPickups = (employeeId: number | null, scope: TeamScope, dateType: DateType, enabled: boolean) =>
  useQuery({
    queryKey: teamKeys.pickups(employeeId, scope, dateType),
    queryFn: () => {
      const { fromDate, toDate } = pickupWindow(dateType);
      return teamDashboardApi.getPickups(employeeId, scope, fromDate, toDate);
    },
    enabled,
    placeholderData: (previous) => previous,
    ...common,
  });

export const useTeamVessels = (employeeId: number | null, scope: TeamScope, dateType: DateType, enabled: boolean) =>
  useQuery({
    queryKey: teamKeys.vessels(employeeId, scope, dateType),
    queryFn: () => {
      const { fromDate, toDate } = vesselWindow(dateType);
      return teamDashboardApi.getVessels(employeeId, scope, fromDate, toDate);
    },
    enabled,
    placeholderData: (previous) => previous,
    ...common,
  });

export const useTeamCounts = (employeeId: number | null, scope: TeamScope, enabled: boolean) =>
  useQuery({
    queryKey: teamKeys.counts(employeeId, scope),
    queryFn: () => {
      const { fromDate, toDate } = monthToDate();
      return teamDashboardApi.getJobCounts(employeeId, scope, fromDate, toDate, UNBILLED_SINCE);
    },
    enabled,
    placeholderData: (previous) => previous,
    ...common,
  });

export const useTeamStatus = (employeeId: number | null, scope: TeamScope, enabled: boolean) =>
  useQuery({
    queryKey: teamKeys.status(employeeId, scope),
    queryFn: () => teamDashboardApi.getStatus(employeeId, scope),
    enabled,
    placeholderData: (previous) => previous,
    ...common,
  });
